import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode
} from 'react';

interface LocationValue {
  pathname: string;
  search: string;
}

type NavigateOptions = { replace?: boolean };
type Navigate = (to: string, options?: NavigateOptions) => void;

interface RouterValue extends LocationValue {
  navigate: Navigate;
}

const RouterContext = createContext<RouterValue | null>(null);

function currentLocation(): LocationValue {
  return {
    pathname: window.location.pathname,
    search: window.location.search
  };
}

export function BrowserRouter({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(currentLocation);

  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  const navigate = useCallback<Navigate>((to, options) => {
    if (options?.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setLocation(currentLocation());
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      ...location,
      navigate
    }),
    [location, navigate]
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter(): RouterValue {
  const context = useContext(RouterContext);
  if (!context) throw new Error('BrowserRouter nedostaje.');
  return context;
}

export function useNavigate(): Navigate {
  return useRouter().navigate;
}

export function useLocation(): LocationValue {
  const { pathname, search } = useRouter();
  return { pathname, search };
}

export function useSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams | Record<string, string>) => void
] {
  const { pathname, search, navigate } = useRouter();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  return [
    params,
    (next) => {
      const nextParams =
        next instanceof URLSearchParams ? next : new URLSearchParams(next);
      const query = nextParams.toString();
      navigate(`${pathname}${query ? `?${query}` : ''}`, { replace: true });
    }
  ];
}

interface NavLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> {
  to: string;
  end?: boolean;
  className?: string | ((state: { isActive: boolean }) => string);
}

export function NavLink({
  to,
  end = false,
  className,
  onClick,
  children,
  ...props
}: NavLinkProps) {
  const { pathname, navigate } = useRouter();
  const targetPath = to.split('?')[0] ?? to;
  const isActive = end
    ? pathname === targetPath
    : pathname === targetPath || pathname.startsWith(`${targetPath}/`);
  const resolvedClass =
    typeof className === 'function' ? className({ isActive }) : className;

  return (
    <a
      {...props}
      href={to}
      className={[resolvedClass, isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

export function Navigate({
  to,
  replace = false
}: {
  to: string;
  replace?: boolean;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, replace, to]);
  return null;
}
